/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import endpoints from "constants/api/Endpoints";
import { post } from "constants/api/ApiClient";
import { SignUpData, SubscriptionSetupData } from "constants/api/models/SignUpResponse";
import ChangePlan from "interface/ChangePlan";
import EndpointProps from "interface/EndpointProps";
import SubscriptionChangePreview from "interface/SubscriptionChangePreview";
import SubscriptionInvoice from "interface/SubscriptionInvoice";
import UserSubscription from "interface/UserSubscription";
import { logError } from "services/logService/errorLogger";
import SubscriptionChangeResult from "interface/SubscriptionChangeResult";
import ResumeSubscriptionResult from "interface/ResumeSubscriptionResult";
import AccountType from "interface/AccountType";
import { PlanName, PlanStatusPayload, StatusRecord } from "interface/FetchAllPlanStatusesOpts";
import accountTypes from "constants/data/AccountTypes";

const fileName = 'SubscriptionApiService'

/**
 * Processes a subscription plan change for a user.
 * 
 * @param {EndpointProps & { data: ChangePlan }} param0 - The request properties, including a tag and the change plan data.
 * @returns {Promise<{ data: SignUpData } | { error: string | null }>} A promise resolving to either the updated sign-up data or an error message.
 * 
 * @throws Will log an error if the process fails due to validation issues, server errors, or unexpected errors.
 * 
 * @example
 * const result = await processChangePlan({
 *   tag: "UpdateSubscription",
 *   data: {
 *     planId: "premium",
 *     userId: "12345",
 *   },
 * });
 * if (result.error) {
 *   console.error("Error:", result.error);
 * } else {
 *   console.log("Success:", result.data);
 * }
 */
export async function processChangePlan({
    tag,
    data,
}: EndpointProps & { data: ChangePlan }): Promise< { data: SignUpData } | { error: string | null }> {
    if (!data) {
        return {
            error: "Please provide all required details to update your account.",
        };
    }

    try {
        const result = await endpoints.updateSubscriptionPlan({ tag, data });

        if (result.error) {
            logError("Error creating user account.", { result }, tag);

            if (typeof result.error === "string") {
                const serverError = result.error.toLowerCase();
                if (serverError.includes("validation")) {
                    return {
                        error: "Some required fields are missing or invalid. Please check your details and try again. You can also update your details in your profile..",
                    };
                }
                return {
                    error: "We encountered an issue while processing your request. Please try again. You can still log in and manage your profile.",
                };
            }

            return {
                error: "An unexpected error occurred while processing your request. Please try again later. You can log in and update your profile in the meantime.",
            };
        }

        const defaultResponse: SignUpData = {} as SignUpData
        const formattedResponse: SignUpData = (result.data as unknown as SignUpData) ?? defaultResponse

        return { data: formattedResponse }
    } catch (error) {
        logError(
            "Unexpected error during signup.",
            { error: error instanceof Error ? error.message : "Unknown error" },
            tag
        );
        return {
            error: "We are unable to process your request at the moment. Please check your connection and try again later. You can still log in and update your profile.",
        };
    }
}

/**
 * Fetches the user's current subscription details.
 * 
 * @returns {Promise<UserSubscription | null>} A promise resolving to the user's subscription data if successful, or null if an error occurs.
 * 
 * @throws Logs an error if the retrieval process encounters any issues.
 * 
 * @example
 * const subscription = await fetchUserSubscription();
 * if (subscription) {
 *   console.log("User subscription:", subscription);
 * } else {
 *   console.error("Failed to fetch subscription.");
 * }
 */
export const fetchUserSubscription = async (): Promise<UserSubscription | null> => {
    try {
        const result = await endpoints.fetchSubscriptionData({ tag: fileName })
        if (result.success && result.data) {
            return result.data
        }
        return null
    } catch (error) {
        logError(
            'Error retrieving subscription.',
            { error },
            fileName
        )
        return null
    }
}

/**
 * Fetches the user's subscription invoices based on the specified status.
 * 
 * @param {EndpointProps & { status: 'succeeded' | 'processing' | 'canceled' | 'failed' }} param0 - The endpoint properties and invoice status.
 * @returns {Promise<SubscriptionInvoice[]>} A promise resolving to an array of subscription invoices.
 * 
 * @throws Logs an error if the fetch operation fails.
 * 
 * @example
 * const invoices = await fetchSubscriptionInvoices({ tag: 'SubscriptionInvoices', status: 'succeeded' });
 * console.log('Invoices:', invoices);
 * */
export async function fetchSubscriptionInvoices({
    tag,
    status,
}: EndpointProps & { status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' }): Promise<SubscriptionInvoice[]> {
    try {
        const result = await endpoints.getSubscriptionInvoices({ tag, status })
        if (result.success && result.data) {
            return result.data ? result.data : []
        }
        logError(
            'Failed to fetch subscription invoices.',
            { error: result.error },
            tag
        )
        return []
    } catch (error) {
        logError(
            'Error fetching subscription invoices.',
            { error },
            tag
        )
        return []
    }
}

/**
 * Initiates the checkout process for a subscription.
 * 
 * @param {EndpointProps} param0 - The endpoint properties.
 * @returns {Promise<SubscriptionSetupData | null>} A promise resolving to the subscription setup data if successful, or null if an error occurs.
 * 
 * @throws Logs an error if the checkout process encounters any issues.
 * 
 * @example
 * const checkoutResult = await checkOutSubscription({ tag: 'CheckoutSubscription' });
 * if (checkoutResult) {
 *   console.log("Checkout initiated:", checkoutResult);
 * } else {
 *   console.error("Failed to initiate checkout.");
 * }
 */
export async function checkOutSubscription({ tag} : EndpointProps): Promise<SubscriptionSetupData | null> {
    try {
        const result = await endpoints.checkOutSubscription({ tag })
        if (result.success && result.data) {
            return result.data
        }
        logError(
            'Failed to fetch checkout subscription.',
            { error: result.error },
            tag
        )
        return null
    } catch (error) {
        logError(
            'Error fetching checkout subscription.',
            { error },
            tag
        )
        return null
    }
}

/**
 * Charges the subscription immediately.
 * @param {EndpointProps & { data: { paymentMethodId: string } }} param0 - The endpoint properties and payment method ID.
 * @returns {Promise<{ paymentIntentClientSecret: string | null }>} A promise resolving to the payment intent client secret or null.
 */
export async function chargeSubscriptionNow({ tag, data }: EndpointProps & { data: { paymentMethodId: string } }): Promise<{ paymentIntentClientSecret: string | null }> {
    try {
        const result = await endpoints.chargeSubscriptionNow({ tag, data })
        if (result.success && result.data && result.data.paymentIntentClientSecret) {
            return { paymentIntentClientSecret: result.data.paymentIntentClientSecret }
        }
        return { paymentIntentClientSecret: null }
    } catch (error) {
        logError(
            'Error charging subscription now.',
            { error },
            tag
        )
        return { paymentIntentClientSecret: null }
    }
}

/**
 * Cancels a subscription.
 * @param {EndpointProps & { data: { reason: string } }} param0 - The endpoint properties and cancellation reason.
 * @returns {Promise<{ success: boolean; message?: string }>} A promise resolving to the cancellation result.
 */
export async function cancelSubscription({ tag, data }: EndpointProps & { data: { reason: string } }): Promise<{ success: boolean; error?: string }> {
    try {
        const result = await endpoints.cancelSubscription({ tag, data })
        if (result.success) {
            return { success: true }
        }
        logError(
            'Failed to cancel subscription.',
            { error: result.error },
            tag
        )
        return { success: false, error: 'Failed to cancel subscription. Please try again later.' }
    } catch (error) {
        logError(
            'Error canceling subscription.',
            { error },
            tag
        )
        return { success: false, error: 'An error occurred while canceling your subscription. Please try again later.' }
    }
}

/** * Previews the changes to a subscription plan.
 * 
 * @param {EndpointProps & { data: ChangePlan }} param0 - The endpoint properties and change plan data.
 */
export async function previewSubscriptionChange({ tag, data }: EndpointProps & { data: ChangePlan }): Promise<{ preview: SubscriptionChangePreview | null; error: string | null }> {
    try {
        const result = await endpoints.previewSubscriptionChange({ tag, data })
        if (result.success && result.data && result.data.code) {
            return { preview: result.data, error: null }
        }
        return { preview: null, error: 'Failed to fetch plan preview. Please try again later.' }
    } catch (error) {
        logError(
            'Error fetching plan preview.',
            { error },
            tag
        )
        return { preview: null, error: 'An error occurred while fetching the plan preview. Please try again later.' }
    }
}

/** Changes the user's subscription plan.
 * 
 * @param {EndpointProps & { data: ChangePlan }} param0 - The endpoint properties and change plan data.
 * @return {Promise<{ success: boolean; error?: string; data?: SubscriptionChangeResult | null }>} A promise resolving to the result of the subscription change.
 * @throws Logs an error if the change process encounters any issues.
 * 
 * @example
 * const result = await changeSubscription({ tag: 'ChangeSubscription', data: { planId: 'newPlanId', userId: 'userId' } });
 * if (result.success) {
 *   console.log('Subscription changed successfully:', result.data);
 * } else {
 *   console.error('Error changing subscription:', result.error);
 * }
 */
export async function changeSubscription({ tag, data }: EndpointProps & { data: ChangePlan }): Promise<{ success: boolean; error?: string; data?: SubscriptionChangeResult | null }> {
    try {
        const result = await endpoints.changeSubscription({ tag, data })
        if (result.success && result.data) {
            return { success: true, data: result.data }
        }
        return { success: false, error: 'Failed to change subscription. Please try again later.' }
    } catch (error) {
        logError(
            'Error changing subscription.',
            { error },
            tag
        )
        return { success: false, error: 'An error occurred while changing your subscription. Please try again later.' }
    }
}

/** Resumes a canceled subscription.
 * 
 * @param {EndpointProps} param0 - The endpoint properties.
 * @return {Promise<{ success: boolean; error?: string; data?: ResumeSubscriptionResult | null }>} A promise resolving to the result of the resume operation.
 * 
 * @throws Logs an error if the resume process encounters any issues.
 * @example
 * const result = await resumeSubscription({ tag: 'ResumeSubscription' });
 * if (result.success) {
 *   console.log('Subscription resumed successfully:', result.data);
 * } else {
 *   console.error('Error resuming subscription:', result.error);
 * }
 */
export async function resumeSubscription({ tag }: EndpointProps): Promise<{ success: boolean; error?: string; data?: ResumeSubscriptionResult | null }> {
    try {
        const result = await endpoints.resumeSubscription({ tag })
        if (result.success && result.data) {
            return { success: true, data: result.data }
        }
        return { success: false, error: 'Failed to resume subscription. Please try again later.' }
    } catch (error) {
        logError(
            'Error resuming subscription.',
            { error },
            tag
        )
        return { success: false, error: 'An error occurred while resuming your subscription. Please try again later.' }
    }
}

export async function fetchPlanStatus({ tag, name }: EndpointProps & { name: keyof AccountType['name'] }): Promise<{ active: boolean; }> {
    try {
        const result = await endpoints.checkPlanStatus({ tag, name })
        if (result.success && result.data) {
            return { active: result.data.active }
        }
        return { active: false }
    } catch (error) {
        logError(
            'Error checking plan status.',
            { error },
            tag
        )
        return { active: false }
    }
}

function emptyStatuses(): { statuses: StatusRecord, active: PlanName []} {
    const statuses: StatusRecord = Object.fromEntries(
        accountTypes.map(({ name }) => [name, false] as const)
    ) as StatusRecord;
    return { statuses, active: [] }
}

/**
 * Fetches the status of all subscription plans.
 * 
 * @param param0 - The endpoint properties.
 * @returns A promise resolving to the status of all subscription plans.
 * 
 * @throws Logs an error if the fetch operation fails.
 * 
 * @example
 * const { statuses } = await fetchAllPlanStatuses({ tag: 'FetchAllPlanStatuses' });
 * console.log('Plan statuses:', statuses);
 * // Example output: { free: true, basic: false, sapphire: true, gold: false, platinum: true, sandbox: false }
 */
export async function fetchAllPlanStatuses({ tag }: EndpointProps): Promise<{ statuses: StatusRecord, active: PlanName []}> {
    try {
        const result = await endpoints.checkAllPlanStatuses({ tag })
        if (!(result?.success && result?.data)) {
            return emptyStatuses()
        }

        const raw = result.data as PlanStatusPayload

        const base: StatusRecord = Object.fromEntries(
            accountTypes.map(({ name }) => [name, false] as const)
        ) as StatusRecord;

        for (const { name } of accountTypes) {
            const entry = raw[name];
            base[name] = Boolean(entry?.active)
        }

        const active = (Object.keys(base) as PlanName[]).filter((key) => base[key]);
        return { statuses: base, active }
    } catch (error) {
        logError(
            'Error checking all plan statuses.',
            { error },
            tag
        )
        return emptyStatuses()
    }
}

/**
 * Creates a Stripe SetupIntent via the v2 subscription endpoint.
 * Step 1 of both Flow A (subscribe) and Flow B (update payment method).
 * The frontend uses the returned clientSecret with stripe.confirmCardSetup().
 *
 * @param purpose - 'subscribe' for new subscriptions; 'update_payment_method' for past_due.
 */
export async function createV2SetupIntent({ tag, purpose }: EndpointProps & { purpose: 'subscribe' | 'update_payment_method' }): Promise<{ clientSecret: string; setupIntentId: string } | null> {
    try {
        const result = await post<{ clientSecret: string; setupIntentId: string }>({
            tag,
            url: '/subscription/v2/setup',
            body: { purpose }
        })
        // handleFetchResponse wraps the full server body as result.response.data,
        // so the actual payload is one level deeper at result.response.data.data
        const serverBody = result.response.data as any
        if (result.response.status === 'success' && serverBody?.data) {
            const data = serverBody.data as { clientSecret: string; setupIntentId: string }
            return { clientSecret: data.clientSecret, setupIntentId: data.setupIntentId }
        }
        logError('v2 setup intent failed.', { status: result.response.status, message: result.response.message }, tag)
        return null
    } catch (error) {
        logError('Error creating v2 setup intent.', { error }, tag)
        return null
    }
}

/**
 * Activates a new subscription after the card has been confirmed via stripe.confirmCardSetup().
 * Flow A only. Cancels any existing subscriptions before creating the new one.
 *
 * @param setupIntentId - The seti_... ID returned by createV2SetupIntent.
 */
export async function activateV2Subscription({ tag, setupIntentId }: EndpointProps & { setupIntentId: string }): Promise<{ success: boolean; error?: string }> {
    try {
        const result = await post<{ subscriptionId: string }>({
            tag,
            url: '/subscription/v2/activate',
            body: { setupIntentId }
        })
        return { success: result.response.status === 'success' }
    } catch (error) {
        logError('Error activating v2 subscription.', { error }, tag)
        return { success: false, error: 'An error occurred. Please try again later.' }
    }
}

/**
 * Updates the default payment method and retries the latest unpaid invoice.
 * Flow B only. Use when the subscription is past_due or the user wants to swap cards.
 *
 * @param setupIntentId - The seti_... ID returned by createV2SetupIntent.
 * @returns paymentSuccess — true if the invoice retry succeeded; false if Stripe will retry later.
 */
export async function updateV2PaymentMethod({ tag, setupIntentId }: EndpointProps & { setupIntentId: string }): Promise<{ success: boolean; paymentSuccess: boolean; error?: string }> {
    try {
        const result = await post<{ paymentSuccess: boolean; invoiceStatus: string | null }>({
            tag,
            url: '/subscription/v2/payment-method/update',
            body: { setupIntentId }
        })
        if (result.response.status === 'success' && result.response.data) {
            return { success: true, paymentSuccess: result.response.data.paymentSuccess }
        }
        return { success: false, paymentSuccess: false }
    } catch (error) {
        logError('Error updating v2 payment method.', { error }, tag)
        return { success: false, paymentSuccess: false, error: 'An error occurred. Please try again later.' }
    }
}

/**
 * Asks the backend to check Stripe and sync the DB if the subscription is active.
 * Called on mount of SubscriptionPaymentForm to recover from missed webhooks.
 */
export async function syncSubscriptionFromStripe({ tag }: EndpointProps): Promise<{ synced: boolean; paymentStatus: string } | null> {
    try {
        const result = await post<{ synced: boolean; paymentStatus: string }>({
            tag,
            url: '/subscription/v2/sync',
            body: {}
        })
        // handleFetchResponse wraps the full server body as result.response.data,
        // so the actual payload is one level deeper at result.response.data.data
        const serverBody = result.response.data as any
        if (result.response.status === 'success' && serverBody?.data) {
            return serverBody.data as { synced: boolean; paymentStatus: string }
        }
        return null
    } catch (error) {
        logError('Error syncing subscription from Stripe.', { error }, tag)
        return null
    }
}

/* eslint-enable @typescript-eslint/no-redundant-type-constituents */
/* eslint-enable @typescript-eslint/no-unsafe-return */
/* eslint-enable @typescript-eslint/no-unsafe-argument */